import { useEffect } from 'react';
import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button } from '@peacecover/component';
import useUserQuery from '../../../presentation/hooks/queries/useUserQuery';
import { useGetBettieProducts } from '../../../domains/bettie/ui/hooks/useGetBettieProducts';
import { openNewTab } from '../../../utils/UIUtils';
import BettieAgreementSection from '../../../presentation/chat/components/BettieAgreementSection';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';

interface BettieCreateRoomTicketBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const BettieCreateRoomTicketBottomSheet = ({
  isOpen,
  onClose
}: BettieCreateRoomTicketBottomSheetProps) => {
  const { colorMode } = useColorMode();
  const { data: bettieProducts } = useGetBettieProducts({
    assetType: 'bettie_chat_room_ticket'
  });
  const { data: user, refetch: refetchUser } = useUserQuery();

  useEffect(() => {
    refetchUser();
    const onFocus = () => {
      refetchUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchUser]);

  if (!bettieProducts?.length || !user) return null;

  const productItem = bettieProducts[0];

  const handleClick = () => {
    const amount = Number(productItem.baseAmount - productItem.discountAmount);
    if (user?.identified) {
      openNewTab(
        `/payment/inicis?amount=${amount}&productName=${productItem.name}&productId=${productItem.bettieProductId}`
      );
    } else {
      openNewTab(
        `/identity-verification/intro?amount=${amount}&productName=${productItem.name}&productId=${productItem.bettieProductId}`
      );
    }
    onClose();
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container className="!rounded-t-[20px] !bg-background-light dark:!bg-background-elevated">
        <div className="px-[24px] py-[40px] flex flex-col gap-[32px]">
          <div
            className="p-[1px] rounded-[18px]"
            style={{
              background:
                BettieGradientColor.bettieProductOutline[colorMode as 'light' | 'dark'].background
            }}
          >
            <div
              className="px-[20px] py-[18px] rounded-[17px] z-10 flex items-center justify-between"
              style={{
                background:
                  BettieGradientColor.bettieProductFill[colorMode as 'light' | 'dark'].background
              }}
            >
              <div className="flex flex-col gap-[5px]">
                <p className="text-[16px] font-semibold leading-[19px] text-text-main-lv2">
                  {productItem.name}
                </p>
                {productItem.description && (
                  <p className="text-[12px] leading-[14px] font-medium text-text-sub-lv1">
                    {productItem.description}
                  </p>
                )}
              </div>
              <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv2">
                {t('BettieChargeBottomSheet.amount', {
                  amount: Number(
                    productItem.baseAmount - productItem.discountAmount
                  ).toLocaleString()
                })}
              </p>
            </div>
          </div>
          <BettieAgreementSection />
        </div>
        <Button
          size="large"
          className="mx-[24px] rounded-[18px] text-[17px] font-semibold leading-[20px]"
          onClick={handleClick}
        >
          {t('BettieChargeBottomSheet.goToPayment')}
        </Button>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
    </Sheet>
  );
};

export default BettieCreateRoomTicketBottomSheet;
